import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { csrfHeaders } from '../utils/csrf';
import { useToast } from './Toast';

interface ScanSummary {
  scanId: string;
  ownerEmail?: string | null;
  timestamp: string;
  profile: string;
  summary: {
    pass: boolean;
    failCount: number;
    warnCount: number;
  };
  metadata?: {
    filename?: string;
    dimensions?: string;
    width?: number;
    height?: number;
    fileCount?: number;
    zipSize?: number;
  };
}

function ScanCardSkeleton() {
  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div className="skeleton" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
          <div className="skeleton" style={{ width: '200px', height: '18px' }} />
          <div className="skeleton" style={{ width: '60px', height: '20px' }} />
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div className="skeleton" style={{ width: '120px', height: '14px' }} />
          <div className="skeleton" style={{ width: '140px', height: '14px' }} />
        </div>
      </div>
    </div>
  );
}

export function HistoryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scans, setScans] = useState<ScanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'name-asc'>('newest');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pass' | 'fail'>('all');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(apiUrl('/auth/me'), { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setUserEmail(data.email || null);
        }
      } catch {}
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const fetchScans = async () => {
      try {
        setLoading(true);
        const mineParam = showMineOnly ? '&mine=true' : '';
        const response = await fetch(apiUrl(`/scans?limit=50${mineParam}`), { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch scan history');
        const data = await response.json();
        setScans(data.scans || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load history');
      } finally {
        setLoading(false);
      }
    };
    fetchScans();
  }, [showMineOnly]);

  const handleDeleteScan = async (scanId: string) => {
    if (!confirm('Delete this scan?')) return;
    try {
      const response = await fetch(apiUrl(`/scans/${scanId}`), {
        method: 'DELETE',
        headers: csrfHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setScans(scans.filter(s => s.scanId !== scanId));
        toast('Scan deleted', 'success');
      } else {
        const data = await response.json();
        toast(data.error || 'Failed to delete scan', 'error');
      }
    } catch {
      toast('Failed to delete scan', 'error');
    }
  };

  const deleteAllMine = async () => {
    if (!confirm('Delete ALL your scans? This cannot be undone.')) return;
    try {
      const response = await fetch(apiUrl('/scans'), {
        method: 'DELETE',
        headers: csrfHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        if (userEmail) {
          setScans(scans.filter(s => s.ownerEmail?.toLowerCase() !== userEmail.toLowerCase()));
        }
        toast('All your scans deleted', 'success');
      } else {
        const data = await response.json();
        toast(data.error || 'Failed to delete scans', 'error');
      }
    } catch {
      toast('Failed to delete scans', 'error');
    }
  };

  const isOwner = (scan: ScanSummary): boolean => {
    if (!userEmail || !scan.ownerEmail) return false;
    return scan.ownerEmail.toLowerCase() === userEmail.toLowerCase();
  };

  // Filter by search + status
  const filteredScans = scans.filter(scan => {
    const filename = (scan.metadata?.filename || `Scan ${scan.scanId.substring(0, 8)}`).toLowerCase();
    const matchesSearch = filename.includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'pass' && scan.summary.pass) || 
      (statusFilter === 'fail' && !scan.summary.pass);
    return matchesSearch && matchesStatus;
  });

  // Sort
  const sortedScans = [...filteredScans].sort((a, b) => {
    if (sortOrder === 'newest') return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    if (sortOrder === 'oldest') return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    if (sortOrder === 'name-asc') {
      const aName = (a.metadata?.filename || `Scan ${a.scanId.substring(0, 8)}`).toLowerCase();
      const bName = (b.metadata?.filename || `Scan ${b.scanId.substring(0, 8)}`).toLowerCase();
      return aName.localeCompare(bName);
    }
    return 0;
  });

  // Counts for filter badges
  const passCount = scans.filter(s => s.summary.pass).length;
  const failCount = scans.filter(s => !s.summary.pass).length;

  if (loading) {
    return (
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div className="skeleton" style={{ width: '200px', height: '28px', marginBottom: '24px' }} />
        <div className="skeleton" style={{ width: '100%', height: '40px', marginBottom: '20px' }} />
        <div style={{ display: 'grid', gap: '16px' }}>
          {[1,2,3,4,5].map(i => <ScanCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--text-primary)' }}>Error loading history</h2>
        <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        <button onClick={() => navigate('/')}>Back to Upload</button>
      </div>
    );
  }

  const filterBtnStyle = (active: boolean) => ({
    padding: '6px 14px',
    backgroundColor: active ? '#3b82f6' : 'var(--nav-inactive-bg)',
    color: active ? '#fff' : 'var(--nav-inactive-text)',
    border: active ? 'none' : '1px solid var(--nav-inactive-border)',
    borderRadius: '6px',
    cursor: 'pointer' as const,
    fontWeight: 500,
    fontSize: '13px',
  });

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        @media (max-width: 640px) {
          .history-search-bar { flex-direction: column !important; }
          .history-search-input { width: 100% !important; flex: none !important; min-width: auto !important; }
          .history-sort-select { width: 100%; }
        }
      `}</style>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '16px', color: 'var(--text-primary)' }}>
          Scan History ({scans.length})
        </h2>

        {/* Search, Sort, and Filter Bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }} className="history-search-bar">
          <div style={{ flex: '1 1 250px', minWidth: '200px' }} className="history-search-input">
            <input
              type="text"
              placeholder="Search by filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--input-border)',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: 'var(--input-bg)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest' | 'name-asc')}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--input-border)',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'var(--input-bg)',
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
            className="history-sort-select"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name-asc">Name A-Z</option>
          </select>
        </div>

        {/* Status Filter */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setStatusFilter('all')} style={filterBtnStyle(statusFilter === 'all')}>
            All ({scans.length})
          </button>
          <button onClick={() => setStatusFilter('pass')} style={filterBtnStyle(statusFilter === 'pass')}>
            Passed ({passCount})
          </button>
          <button onClick={() => setStatusFilter('fail')} style={filterBtnStyle(statusFilter === 'fail')}>
            Failed ({failCount})
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '24px', gap: '12px', flexWrap: 'wrap' }}>
        {userEmail && (
          <button
            onClick={() => setShowMineOnly(!showMineOnly)}
            style={{
              padding: '8px 16px',
              backgroundColor: showMineOnly ? '#7c3aed' : 'var(--nav-inactive-bg)',
              color: showMineOnly ? '#fff' : 'var(--nav-inactive-text)',
              border: showMineOnly ? 'none' : '1px solid var(--nav-inactive-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px'
            }}
          >
            {showMineOnly ? 'My Scans' : 'All Scans'}
          </button>
        )}
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          New Scan
        </button>
        {userEmail && scans.some(s => isOwner(s)) && (
          <button
            onClick={deleteAllMine}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Delete My Scans
          </button>
        )}
      </div>

      {sortedScans.length === 0 && scans.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '8px',
          border: '1px dashed var(--border-color)'
        }}>
          <h3 style={{ color: 'var(--text-primary)' }}>{showMineOnly ? 'No scans found for your account' : 'No scan history yet'}</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            {showMineOnly ? 'Upload a creative while logged in to see your scans here' : 'Upload your first HTML5 creative to get started'}
          </p>
          <button onClick={() => navigate('/')} style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
            Upload Creative
          </button>
        </div>
      ) : sortedScans.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '8px',
          border: '1px dashed var(--border-color)'
        }}>
          <h3 style={{ color: 'var(--text-primary)' }}>No scans match your filters</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>Try adjusting your search or filter settings</p>
          <button
            onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
            style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {sortedScans.map(scan => (
            <div
              key={scan.scanId}
              style={{
                padding: '20px',
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              onClick={() => navigate(`/scans/${scan.scanId}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-hover)';
                e.currentTarget.style.boxShadow = '0 4px 6px var(--shadow-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: scan.summary.pass ? '#10b981' : '#ef4444'
                  }}>
                    {scan.summary.pass ? '\u2713' : '\u2717'}
                  </span>
                  <span style={{ fontWeight: 500, fontSize: '16px', color: 'var(--text-primary)' }}>
                    {scan.metadata?.filename || `Scan ${scan.scanId.substring(0, 8)}...`}
                  </span>
                  {scan.metadata?.dimensions && (
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: 'var(--badge-dim-bg)',
                      color: 'var(--badge-dim-text)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      {scan.metadata.dimensions}
                    </span>
                  )}
                  <span style={{
                    padding: '2px 8px',
                    backgroundColor: scan.summary.pass ? 'var(--badge-pass-bg)' : 'var(--badge-fail-bg)',
                    color: scan.summary.pass ? 'var(--badge-pass-text)' : 'var(--badge-fail-text)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    {scan.summary.pass ? 'PASSED' : `${scan.summary.failCount} FAILED`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                  <span>Profile: <strong>{scan.profile}</strong></span>
                  <span>|</span>
                  <span>{new Date(scan.timestamp).toLocaleString()}</span>
                  {scan.summary.warnCount > 0 && (
                    <>
                      <span>|</span>
                      <span>{scan.summary.warnCount} warnings</span>
                    </>
                  )}
                </div>
              </div>
              {isOwner(scan) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteScan(scan.scanId);
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--badge-fail-bg)',
                    color: 'var(--badge-fail-text)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{
        marginTop: '40px',
        padding: '16px',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '8px',
        fontSize: '13px',
        color: 'var(--text-secondary)'
      }}>
        <p style={{ margin: 0 }}>
          <strong>Note:</strong> Scans are automatically deleted after 30 days.
          {userEmail ? ' Use the "My Scans" toggle to filter to only your uploads.' : ' Log in to manage and track your own scans.'}
        </p>
      </div>
    </div>
  );
}
